/**
 * The type-specific half of a `propose_relationship` agent's settings page:
 * which relationship types it proposes and how sure it must be. Stored in
 * agent_settings.behavior_config.
 *
 * Nexus and every custom agent made on its behavior show this same panel; the
 * rest of the page is the template's common switches.
 */

import { Select } from '../../ds';
import { resolveRelationshipConfig } from '../../../lib/relationshipRunner';
import { RELATIONSHIP_TYPES, type RelationshipTypeKey } from '../../../../supabase/functions/_shared/relationshipCore.ts';
import type { RelationshipBehaviorConfig } from '../../../lib/ai/agentDefinitions';
import { ChipPicker } from '../template/shared';
import type { BehaviorSettingsPanelProps } from './types';

const TYPE_WORDS: Record<RelationshipTypeKey, string> = {
  dependency: 'Dependency',
  prerequisite: 'Prerequisite',
  aggregation: 'Part of a whole',
  breakdown: 'Breakdown',
  elaboration: 'Elaboration',
  package: 'Package',
  related: 'Related',
};

export function RelationshipSettings({ settings, isSaving, saveBehaviorConfig }: BehaviorSettingsPanelProps) {
  const behavior = resolveRelationshipConfig(settings);
  const saveBehavior = (patch: Partial<RelationshipBehaviorConfig>) => saveBehaviorConfig({ ...behavior, ...patch });
  const toggleType = (key: string) => {
    const on = behavior.types.includes(key as RelationshipTypeKey);
    const next = on ? behavior.types.filter((t) => t !== key) : [...behavior.types, key as RelationshipTypeKey];
    if (next.length) void saveBehavior({ types: next });
  };

  return (
    <div className="card space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-[var(--text-pri)]">What it proposes</h3>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">The settings that belong to this type of agent. Duplicates and variants are Twins' job and are never proposed here.</p>
      </div>
      <div>
        <p className="text-xs font-medium text-[var(--text-sec)] mb-2">Relationship types</p>
        <ChipPicker
          options={RELATIONSHIP_TYPES.map((t) => ({ key: t, label: TYPE_WORDS[t] }))}
          chosen={behavior.types}
          disabled={isSaving}
          onToggle={toggleType}
        />
        <p className="text-xs text-[var(--text-ter)] mt-1">At least one stays on.</p>
      </div>
      <div className="max-w-sm">
        <p className="text-xs font-medium text-[var(--text-sec)] mb-1">How sure it must be before it proposes</p>
        <Select
          value={behavior.minConfidence}
          disabled={isSaving}
          onChange={(e) => void saveBehavior({ minConfidence: e.target.value as RelationshipBehaviorConfig['minConfidence'] })}
          className="w-full"
        >
          <option value="low">Any confidence — propose every time</option>
          <option value="medium">Medium or high</option>
          <option value="high">High only</option>
        </Select>
      </div>
    </div>
  );
}
